import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value || {}))
}

const OPTION_TYPES = ['single-select', 'multi-select', 'select']

const QUESTION_TYPE_OPTIONS = [
  { value: 'text', label: '단답' },
  { value: 'long-text', label: '장문' },
  { value: 'tel', label: '전화' },
  { value: 'date', label: '날짜' },
  { value: 'single-select', label: '단일 선택' },
  { value: 'multi-select', label: '복수 선택' },
  { value: 'select', label: '드롭다운' },
]

function isOptionType(type) {
  return OPTION_TYPES.includes(String(type || '').toLowerCase())
}

function normalizeOption(opt) {
  if (typeof opt === 'string') {
    return { value: opt, label: opt }
  }
  const v = opt?.value != null ? String(opt.value) : ''
  const l = opt?.label != null ? String(opt.label) : v
  return { value: v || l, label: l || v }
}

function normalizeQuestion(raw) {
  const q = raw && typeof raw === 'object' ? raw : {}
  const type = q.type || 'text'
  const opts = Array.isArray(q.options) ? q.options.map(normalizeOption) : []
  return {
    key: String(q.key || '').trim(),
    type,
    label: String(q.label || ''),
    required: q.required === true,
    placeholder: q.placeholder != null ? String(q.placeholder) : '',
    helpText: q.helpText != null ? String(q.helpText) : '',
    options: isOptionType(type) && opts.length > 0 ? opts : (isOptionType(type) ? [{ value: '보기1', label: '보기1' }, { value: '보기2', label: '보기2' }] : []),
  }
}

function newLocalId() {
  return `qb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function entriesFromSchema(schema) {
  const bank = Array.isArray(schema?.questionBank) ? schema.questionBank : []
  return bank.map((e) => ({
    localId: newLocalId(),
    sectionKey: e.sectionKey != null ? String(e.sectionKey) : 'extras',
    sectionTitle: e.sectionTitle != null ? String(e.sectionTitle) : '추가 문항',
    question: normalizeQuestion(e.question),
  }))
}

function serializeQuestion(q) {
  const out = {
    key: String(q.key || '').trim().replace(/\s+/g, '_'),
    type: q.type || 'text',
    label: String(q.label || ''),
    required: q.required === true,
  }
  if (q.placeholder) out.placeholder = String(q.placeholder)
  if (q.helpText) out.helpText = String(q.helpText)
  if (isOptionType(q.type)) {
    const opts = (Array.isArray(q.options) ? q.options : []).map(normalizeOption).filter((o) => o.value || o.label)
    out.options = opts.map((o) => ({ value: o.value || o.label, label: o.label || o.value }))
  }
  return out
}

function validateEntries(entries) {
  const seen = new Set()
  for (let i = 0; i < entries.length; i++) {
    const k = String(entries[i].question?.key || '').trim()
    if (!k) {
      return `${i + 1}번째 문항의 ID(key)를 입력해 주세요.`
    }
    if (seen.has(k)) {
      return `문항 ID "${k}" 이(가) 중복되었습니다.`
    }
    seen.add(k)
    const q = entries[i].question
    if (isOptionType(q.type)) {
      const opts = Array.isArray(q.options) ? q.options : []
      const ok = opts.some((o) => (normalizeOption(o).value || normalizeOption(o).label))
      if (!ok) {
        return `선택형 문항 "${k}" 에 보기를 1개 이상 입력해 주세요.`
      }
    }
  }
  return null
}

export default function CounselSurveyQuestionBankPanel() {
  const [selectedCategoryCode, setSelectedCategoryCode] = React.useState('ALL')
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(null)
  const [entries, setEntries] = React.useState([])

  const categoriesQuery = useQuery({
    queryKey: ['counsel-surveys', 'categories', 'admin'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/categories', { params: { activeOnly: false }, __silent: true })),
  })

  const templatesQuery = useQuery({
    queryKey: ['counsel-surveys', 'templates', 'admin', selectedCategoryCode],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/templates', {
      params: selectedCategoryCode !== 'ALL' ? { categoryCode: selectedCategoryCode } : {},
      __silent: true,
    })),
  })

  const templateDetailQuery = useQuery({
    queryKey: ['counsel-surveys', 'template-detail', selectedTemplateId],
    enabled: !!selectedTemplateId,
    queryFn: async () => unwrap(await api.get(`/counsel-surveys/templates/${selectedTemplateId}`, { __silent: true })),
  })

  React.useEffect(() => {
    if (!templateDetailQuery.data) {
      setEntries([])
      return
    }
    setEntries(entriesFromSchema(templateDetailQuery.data.schema))
  }, [templateDetailQuery.data])

  const refresh = async () => {
    await Promise.all([categoriesQuery.refetch(), templatesQuery.refetch(), templateDetailQuery.refetch()])
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const item = templateDetailQuery.data
      const schema = deepClone(item.schema || {})
      const bankPayload = entries.map(({ sectionKey, sectionTitle, question }) => ({
        sectionKey: String(sectionKey || 'extras').trim() || 'extras',
        sectionTitle: String(sectionTitle || '').trim() || '추가 문항',
        question: serializeQuestion(question),
      }))
      if (bankPayload.length > 0) {
        schema.questionBank = bankPayload
      } else {
        delete schema.questionBank
      }
      return unwrap(await api.patch(`/counsel-surveys/templates/${item.id}`, {
        name: item.name,
        categoryCode: item.categoryCode,
        description: item.description || '',
        templateScope: item.categoryCode === 'COMMON' ? 'COMMON' : 'CATEGORY',
        publish: item.status === 'PUBLISHED',
        status: item.status,
        schema,
      }))
    },
    onSuccess: async () => {
      try { window?.toast?.show?.('문항 은행을 저장했습니다.', 'success') } catch {}
      await templateDetailQuery.refetch()
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '저장 실패', 'error') } catch {}
    },
  })

  const updateEntry = (localId, patch) => {
    setEntries((prev) => prev.map((row) => (row.localId === localId ? { ...row, ...patch } : row)))
  }

  const updateQuestion = (localId, patch) => {
    setEntries((prev) => prev.map((row) => {
      if (row.localId !== localId) return row
      const nextQ = { ...row.question, ...patch }
      if (patch.type != null && !isOptionType(patch.type)) {
        nextQ.options = []
      } else if (patch.type != null && isOptionType(patch.type) && (!Array.isArray(nextQ.options) || nextQ.options.length === 0)) {
        nextQ.options = [{ value: '보기1', label: '보기1' }, { value: '보기2', label: '보기2' }]
      }
      return { ...row, question: nextQ }
    }))
  }

  const addEntry = () => {
    setEntries((prev) => [...prev, {
      localId: newLocalId(),
      sectionKey: 'extras',
      sectionTitle: '추가 문항',
      question: normalizeQuestion({ key: `qb_${prev.length + 1}`, type: 'text', label: '', required: false }),
    }])
  }

  const removeEntry = (localId) => {
    if (!window.confirm('이 문항을 목록에서 제거할까요?')) return
    setEntries((prev) => prev.filter((r) => r.localId !== localId))
  }

  const moveEntry = (index, dir) => {
    setEntries((prev) => {
      const j = dir === 'up' ? index - 1 : index + 1
      if (j < 0 || j >= prev.length) return prev
      const next = [...prev]
      const t = next[index]
      next[index] = next[j]
      next[j] = t
      return next
    })
  }

  const addOption = (localId) => {
    setEntries((prev) => prev.map((row) => {
      if (row.localId !== localId) return row
      const q = row.question
      const opts = [...(Array.isArray(q.options) ? q.options : []), { value: '', label: '' }]
      return { ...row, question: { ...q, options: opts } }
    }))
  }

  const updateOption = (localId, optIdx, field, value) => {
    setEntries((prev) => prev.map((row) => {
      if (row.localId !== localId) return row
      const q = row.question
      const opts = [...(Array.isArray(q.options) ? q.options : [])]
      opts[optIdx] = { ...normalizeOption(opts[optIdx] || {}), [field]: value }
      return { ...row, question: { ...q, options: opts } }
    }))
  }

  const removeOption = (localId, optIdx) => {
    setEntries((prev) => prev.map((row) => {
      if (row.localId !== localId) return row
      const q = row.question
      const opts = (Array.isArray(q.options) ? q.options : []).filter((_, i) => i !== optIdx)
      return { ...row, question: { ...q, options: opts } }
    }))
  }

  const handleSave = () => {
    const msg = validateEntries(entries)
    if (msg) {
      try { window?.toast?.show?.(msg, 'error') } catch {}
      return
    }
    saveMutation.mutate()
  }

  if (categoriesQuery.isLoading || templatesQuery.isLoading) return <Loading />
  if (categoriesQuery.isError) return <InlineError message={categoriesQuery.error?.message || '카테고리를 불러오지 못했습니다.'} />
  if (templatesQuery.isError) return <InlineError message={templatesQuery.error?.message || '템플릿을 불러오지 못했습니다.'} />

  const categories = Array.isArray(categoriesQuery.data) ? categoriesQuery.data : []
  const templates = Array.isArray(templatesQuery.data) ? templatesQuery.data : []
  const detail = templateDetailQuery.data

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">문항 은행 (questionBank)</h2>
          <div className="mt-1 text-sm text-slate-500">
            본문 설문(sections)에 넣지 않은 추천 문항을 관리합니다. 상담 설문 편집 화면에서 이 목록으로부터 문항을 추가할 수 있습니다.
          </div>
        </div>
        <button type="button" onClick={refresh} className="rounded border px-3 py-2 text-sm hover:bg-slate-50">새로고침</button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(280px,0.95fr)_1.4fr]">
        <section className="rounded-2xl border bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">템플릿 선택</h3>
            <select
              className="max-w-[160px] rounded border px-2 py-1.5 text-sm"
              value={selectedCategoryCode}
              onChange={(e) => { setSelectedCategoryCode(e.target.value); setSelectedTemplateId(null) }}
            >
              <option value="ALL">전체</option>
              {categories.map((item) => (
                <option key={item.id} value={item.code}>{item.name}</option>
              ))}
            </select>
          </div>
          <div className="mt-3 max-h-[min(70vh,520px)] space-y-2 overflow-y-auto">
            {templates.length === 0 && (
              <div className="rounded-xl border border-dashed p-6 text-sm text-slate-400">선택한 카테고리에 템플릿이 없습니다.</div>
            )}
            {templates.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedTemplateId(item.id)}
                className={`block w-full rounded-xl border p-3 text-left ${selectedTemplateId === item.id ? 'border-brand-sky bg-sky-50/40' : 'hover:bg-slate-50'}`}
              >
                <div className="font-medium">{item.name}</div>
                <div className="mt-1 text-xs text-slate-500">{item.categoryCode || 'COMMON'} · {item.status}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border bg-white p-4">
          {!detail && (
            <div className="rounded-xl border border-dashed p-10 text-center text-sm text-slate-500">
              왼쪽에서 고객설문 템플릿을 선택하면 문항 은행을 편집할 수 있습니다.
            </div>
          )}
          {detail && templateDetailQuery.isLoading && <div className="text-sm text-slate-500">불러오는 중…</div>}
          {detail && templateDetailQuery.isError && (
            <InlineError message={templateDetailQuery.error?.message || '템플릿 상세를 불러오지 못했습니다.'} />
          )}
          {detail && !templateDetailQuery.isLoading && !templateDetailQuery.isError && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                <div>
                  <div className="font-semibold">{detail.name}</div>
                  <div className="text-xs text-slate-500">스키마의 title·sections·description 등은 유지되고 questionBank만 갱신됩니다.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={addEntry}
                    className="rounded border border-teal-200 bg-teal-50 px-3 py-1.5 text-sm font-semibold text-teal-800 hover:bg-teal-100"
                  >
                    + 문항 추가
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saveMutation.isPending}
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  >
                    {saveMutation.isPending ? '저장 중…' : '문항 은행 저장'}
                  </button>
                </div>
              </div>

              {entries.length === 0 && (
                <div className="rounded-xl bg-slate-50 p-6 text-sm text-slate-600">
                  등록된 문항이 없습니다. 「문항 추가」로 추천 문항을 넣거나, 고객설문 탭에서 시드된 템플릿을 불러온 뒤 여기서 다듬을 수 있습니다.
                </div>
              )}

              <div className="max-h-[min(65vh,560px)] space-y-4 overflow-y-auto pr-1">
                {entries.map((row, index) => (
                  <div key={row.localId} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-slate-600">문항 {index + 1}</span>
                      <div className="flex flex-wrap gap-1">
                        <button type="button" className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40" disabled={index === 0} onClick={() => moveEntry(index, 'up')}>위로</button>
                        <button type="button" className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40" disabled={index === entries.length - 1} onClick={() => moveEntry(index, 'down')}>아래로</button>
                        <button type="button" className="rounded border bg-white px-2 py-1 text-xs text-rose-600 hover:bg-slate-50" onClick={() => removeEntry(row.localId)}>삭제</button>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600">섹션 키 (sectionKey)</label>
                        <input
                          className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm"
                          value={row.sectionKey}
                          onChange={(e) => updateEntry(row.localId, { sectionKey: e.target.value })}
                          placeholder="예: needs, basic"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600">섹션 제목 (sectionTitle)</label>
                        <input
                          className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm"
                          value={row.sectionTitle}
                          onChange={(e) => updateEntry(row.localId, { sectionTitle: e.target.value })}
                          placeholder="표시용 구역 이름"
                        />
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600">문항 ID (key)</label>
                        <input
                          className="mt-1 w-full rounded border bg-white px-2 py-1.5 font-mono text-sm"
                          value={row.question.key}
                          onChange={(e) => updateQuestion(row.localId, { key: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600">유형</label>
                        <select
                          className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm"
                          value={row.question.type}
                          onChange={(e) => updateQuestion(row.localId, { type: e.target.value })}
                        >
                          {QUESTION_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-slate-600">질문 문구 (label)</label>
                      <input
                        className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm"
                        value={row.question.label}
                        onChange={(e) => updateQuestion(row.localId, { label: e.target.value })}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={row.question.required}
                          onChange={(e) => updateQuestion(row.localId, { required: e.target.checked })}
                        />
                        필수
                      </label>
                    </div>
                    {(row.question.type === 'text' || row.question.type === 'long-text' || row.question.type === 'tel') && (
                      <div className="mt-3">
                        <label className="block text-xs font-semibold text-slate-600">placeholder</label>
                        <input
                          className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm"
                          value={row.question.placeholder}
                          onChange={(e) => updateQuestion(row.localId, { placeholder: e.target.value })}
                        />
                      </div>
                    )}
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-slate-600">도움말 (helpText, 선택)</label>
                      <input
                        className="mt-1 w-full rounded border bg-white px-2 py-1.5 text-sm"
                        value={row.question.helpText}
                        onChange={(e) => updateQuestion(row.localId, { helpText: e.target.value })}
                      />
                    </div>
                    {isOptionType(row.question.type) && (
                      <div className="mt-3 rounded-lg border border-dashed border-slate-200 bg-white p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-600">보기 (value / label)</span>
                          <button type="button" className="rounded border px-2 py-0.5 text-xs hover:bg-slate-50" onClick={() => addOption(row.localId)}>+ 보기</button>
                        </div>
                        <div className="space-y-2">
                          {(row.question.options || []).map((opt, oi) => {
                            const n = normalizeOption(opt)
                            return (
                              <div key={oi} className="flex flex-wrap items-center gap-2">
                                <input
                                  className="min-w-[100px] flex-1 rounded border px-2 py-1 text-xs"
                                  placeholder="value"
                                  value={n.value}
                                  onChange={(e) => updateOption(row.localId, oi, 'value', e.target.value)}
                                />
                                <input
                                  className="min-w-[100px] flex-1 rounded border px-2 py-1 text-xs"
                                  placeholder="label"
                                  value={n.label}
                                  onChange={(e) => updateOption(row.localId, oi, 'label', e.target.value)}
                                />
                                <button type="button" className="rounded border px-2 py-1 text-xs text-rose-600" onClick={() => removeOption(row.localId, oi)}>삭제</button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
